import React, { memo, useCallback, useRef, useState } from "react";
import { Clock, Loader2, AlertTriangle, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { activityApi } from "../lib/api";

const HIZLI = [1, 5, 10, 30];

const OnizlemeSatiri = memo(({ metin, sebep }) => (
  <div className="px-1 py-0.5 border-b border-sertex-cyan/10 last:border-0">
    <div className="text-[11px] font-mono text-sertex-text leading-snug">{metin}</div>
    {sebep && <div className="hud-text text-[9px] text-sertex-textMuted/70">{sebep}</div>}
  </div>
));
OnizlemeSatiri.displayName = "OnizlemeSatiri";

// FAZ E2 — zaman penceresiyle toptan geri alma. Zaman düğmeleri TEK BAŞINA
// yıkıcı değildir: her zaman önizleme + onay gerekir.
export const BulkUndoSection = ({ canSeeAll, onDone }) => {
  const [scope, setScope] = useState("mine");
  const [elle, setElle] = useState("");
  const [yukleniyor, setYukleniyor] = useState(false);
  const [onizleme, setOnizleme] = useState(null);
  const [zorla, setZorla] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  // FAZ F — çift gönderim kilidi (toptan geri alma / iptal bir kez çalışır).
  const islemKilit = useRef(false);

  const onizle = useCallback(async (dk) => {
    setYukleniyor(true);
    setSonuc(null);
    try {
      const d = await activityApi.bulkPreview(dk, scope);
      if (!d.total) {
        toast(d.empty_reason || "Bu zaman aralığında geri alınabilir işlem yok");
        setOnizleme(null);
        return;
      }
      setZorla(false);
      setOnizleme(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme alınamadı");
    } finally {
      setYukleniyor(false);
    }
  }, [scope]);

  const elleOnizle = useCallback(() => {
    const n = Number(elle);
    if (!elle.trim() || Number.isNaN(n) || !Number.isInteger(n)) {
      toast.error("Geçersiz dakika değeri, tam sayı giriniz");
      return;
    }
    if (n <= 0) { toast.error("Dakika sıfır veya negatif olamaz"); return; }
    if (n > 1440) { toast.error("En fazla 24 saat (1440 dakika) geri alınabilir"); return; }
    onizle(n);
  }, [elle, onizle]);

  const uygula = useCallback(async () => {
    if (!onizleme || islemKilit.current) return;
    islemKilit.current = true;
    setYukleniyor(true);
    try {
      const r = await activityApi.bulkUndo(onizleme.minutes, onizleme.scope, zorla);
      setSonuc(r);
      setOnizleme(null);
      toast.success(r.summary);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toptan geri alma başarısız");
    } finally {
      setYukleniyor(false);
      islemKilit.current = false;
    }
  }, [onizleme, zorla, onDone]);

  const iptalEt = useCallback(async () => {
    if (!sonuc?.bulk_id || islemKilit.current) return;
    islemKilit.current = true;
    setYukleniyor(true);
    try {
      const r = await activityApi.bulkCancel(sonuc.bulk_id);
      toast.success(r.summary);
      setSonuc((s) => ({ ...s, cancelled: true }));
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İptal başarısız");
    } finally {
      setYukleniyor(false);
      islemKilit.current = false;
    }
  }, [sonuc, onDone]);

  return (
    <div
      data-testid="bulk-undo-section"
      className="px-2 py-1.5 border-b border-amber-400/20 space-y-1.5"
    >
      <div className="flex items-center gap-1 hud-text text-[10px] text-amber-300">
        <Clock className="h-3 w-3" /> ZAMANA GÖRE GERİ AL
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        {HIZLI.map((dk) => (
          <button
            key={dk}
            type="button"
            disabled={yukleniyor}
            onClick={() => onizle(dk)}
            data-testid={`bulk-undo-${dk}dk`}
            className="px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-200 hover:bg-amber-500/15 hud-text text-[10px] transition-colors disabled:opacity-50"
          >
            {dk} DK
          </button>
        ))}
        <input
          value={elle}
          onChange={(e) => setElle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") elleOnizle(); }}
          placeholder="dk"
          data-testid="bulk-undo-manual-input"
          className="w-12 bg-sertex-surface border border-amber-400/30 rounded px-1 py-0.5 text-[10px] font-mono text-sertex-text outline-none focus:border-amber-400"
        />
        <button
          type="button"
          disabled={yukleniyor}
          onClick={elleOnizle}
          data-testid="bulk-undo-manual-btn"
          className="px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-200 hover:bg-amber-500/15 hud-text text-[10px] transition-colors disabled:opacity-50"
        >
          UYGULA
        </button>
        {yukleniyor && <Loader2 className="h-3 w-3 animate-spin text-amber-300" />}
      </div>

      <select
        value={scope}
        onChange={(e) => setScope(e.target.value)}
        data-testid="bulk-undo-scope"
        className="w-full bg-sertex-surface border border-amber-400/25 rounded px-1 py-0.5 text-[10px] font-mono text-sertex-text outline-none"
      >
        <option value="mine">Sadece benim işlemlerim</option>
        {canSeeAll && <option value="all">Herkesin işlemleri</option>}
      </select>

      {/* ÖNİZLEME + ONAY — atlanamaz */}
      {onizleme && (
        <div
          data-testid="bulk-undo-preview"
          className="rounded border border-amber-400/40 bg-amber-500/5 p-1.5 space-y-1"
        >
          <div className="hud-text text-[10px] text-amber-200">
            SON {onizleme.minutes} DAKİKA · {onizleme.total} İŞLEM GERİ ALINACAK
          </div>
          <div className="text-[10px] font-mono text-sertex-textMuted">
            {Object.entries(onizleme.breakdown || {})
              .map(([k, v]) => `${v} ${k}`)
              .join(", ")}
          </div>
          {onizleme.warning && (
            <div className="flex items-start gap-1 text-[10px] font-mono text-rose-300">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {onizleme.warning}
            </div>
          )}
          <div
            data-testid="bulk-undo-preview-list"
            className="max-h-32 overflow-y-auto rounded bg-sertex-bg/40 px-1"
          >
            {onizleme.items.map((i) => (
              <OnizlemeSatiri key={i.id} metin={i.label} />
            ))}
          </div>
          {!!onizleme.irreversible?.length && (
            <div data-testid="bulk-undo-irreversible">
              <div className="hud-text text-[10px] text-sertex-textMuted flex items-center gap-1">
                <Ban className="h-2.5 w-2.5" />
                {onizleme.irreversible.length} işlem geri alınamaz, atlanacak
              </div>
              <div className="max-h-16 overflow-y-auto">
                {onizleme.irreversible.map((i, idx) => (
                  <OnizlemeSatiri key={idx} metin={i.label} sebep={i.reason} />
                ))}
              </div>
            </div>
          )}
          {!!onizleme.conflicts?.length && (
            <div data-testid="bulk-undo-conflicts">
              <div className="hud-text text-[10px] text-rose-300">
                {onizleme.conflicts.length} öğe daha sonra değiştirildiği için atlanacak
              </div>
              <div className="max-h-16 overflow-y-auto">
                {onizleme.conflicts.map((i) => (
                  <OnizlemeSatiri key={i.id} metin={i.label} sebep={i.reason} />
                ))}
              </div>
              <label className="flex items-center gap-1 hud-text text-[9px] text-sertex-textMuted cursor-pointer">
                <input
                  type="checkbox"
                  checked={zorla}
                  onChange={(e) => setZorla(e.target.checked)}
                  data-testid="bulk-undo-force-conflicts"
                />
                Çakışmalıları da zorla geri al
              </label>
            </div>
          )}
          <div className="flex items-center gap-1 pt-0.5">
            <button
              type="button"
              disabled={yukleniyor}
              onClick={uygula}
              data-testid="bulk-undo-confirm"
              className="flex-1 px-2 py-1 rounded bg-amber-500/20 border border-amber-400/50 text-amber-100 hover:bg-amber-500/30 hud-text text-[10px] transition-colors disabled:opacity-50"
            >
              ONAYLA VE GERİ AL
            </button>
            <button
              type="button"
              onClick={() => setOnizleme(null)}
              data-testid="bulk-undo-cancel"
              className="px-2 py-1 rounded border border-sertex-textMuted/30 text-sertex-textMuted hover:text-sertex-text hud-text text-[10px] transition-colors"
            >
              VAZGEÇ
            </button>
          </div>
        </div>
      )}

      {/* SONUÇ RAPORU + GERİ ALMAYI İPTAL ET */}
      {sonuc && (
        <div
          data-testid="bulk-undo-result"
          className="rounded border border-sertex-cyan/30 bg-sertex-surface/60 p-1.5 space-y-1"
        >
          <div className="text-[10px] font-mono text-sertex-text">{sonuc.summary}</div>
          {!!sonuc.failed?.length && (
            <div data-testid="bulk-undo-failed">
              <div className="hud-text text-[10px] text-rose-300">
                {sonuc.failed.length} işlem teknik hata nedeniyle geri alınamadı:
              </div>
              <div className="max-h-16 overflow-y-auto">
                {sonuc.failed.map((f, i) => (
                  <OnizlemeSatiri key={i} metin={f.label} sebep={f.reason} />
                ))}
              </div>
            </div>
          )}
          {sonuc.can_cancel && (
            <button
              type="button"
              disabled={yukleniyor || sonuc.cancelled}
              onClick={iptalEt}
              data-testid="bulk-undo-revert"
              className="w-full px-2 py-1 rounded border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/10 hud-text text-[10px] transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
            >
              <RotateCcw className="h-2.5 w-2.5" />
              {sonuc.cancelled ? "İPTAL EDİLDİ" : "GERİ ALMAYI İPTAL ET"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkUndoSection;
